<template>
  <div class="hrd">
    <!-- 顶栏 -->
    <div class="hrd-bar">
      <div class="hrd-sizes">
        <button
            v-for="s in sizes"
            :key="s"
            type="button"
            class="hrd-size"
            :class="{ active: size === s }"
            @click="setSize(s)">
          {{ s }}×{{ s }}
        </button>
      </div>
      <div class="hrd-modes">
        <button
            type="button"
            class="hrd-mode"
            :class="{ active: mode === 'normal' }"
            @click="setMode('normal')">普通</button>
        <button
            type="button"
            class="hrd-mode"
            :class="{ active: mode === 'challenge' }"
            @click="setMode('challenge')">限时挑战</button>
      </div>
      <div class="hrd-stats">
        <span class="hrd-stat">步数 <b>{{ moves }}</b></span>
        <span class="hrd-stat" :class="{ danger: mode === 'challenge' && timeLeft <= 10 }">
          {{ mode === 'challenge' ? '⏱ 剩余 ' + timeLeft + 's' : '⏱ ' + seconds + 's' }}
        </span>
        <button
            type="button"
            class="hrd-hint"
            :disabled="hints <= 0 || status !== 'playing'"
            title="AI 提示下一步（每次消耗 1 次）"
            @click="useHint">
          💡 ×{{ hints }}
        </button>
        <button type="button" class="hrd-refresh" title="重新打乱" @click="restart">🔄</button>
      </div>
    </div>

    <div class="hrd-body">
      <!-- 棋盘 -->
      <div class="hrd-board" :style="boardStyle">
        <div
            v-for="(v, i) in board"
            :key="i"
            class="hrd-tile"
            :class="{
              empty: v === 0,
              ok: v !== 0 && isInPlace(i, v),
              hinted: hintTile === i
            }"
            :style="tileStyle(i)"
            @click="tileClick(i)">
          <span v-if="v !== 0" class="hrd-tile-num">{{ v }}</span>
        </div>
      </div>

      <!-- 侧栏 -->
      <div class="hrd-side">
        <div class="hrd-best">
          <p class="hrd-side-title">🏆 最佳纪录</p>
          <p class="hrd-best-line">最少步数 <b>{{ best.moves || '—' }}</b></p>
          <p class="hrd-best-line">最快用时 <b>{{ best.sec ? best.sec + 's' : '—' }}</b></p>
        </div>
        <div class="hrd-target">
          <p class="hrd-side-title">🎯 目标排列</p>
          <div class="hrd-target-grid" :style="boardStyle">
            <div
                v-for="(v, i) in targetBoard"
                :key="i"
                class="hrd-target-cell"
                :class="{ empty: v === 0 }"
                :style="tileStyle(i)">
              <span v-if="v !== 0" class="hrd-tile-num hrd-tile-num-sm">{{ v }}</span>
            </div>
          </div>
        </div>
        <p class="hrd-tip">点击与空格相邻的数字方块移动它～</p>
      </div>
    </div>

    <!-- 过关 / 失败遮罩 -->
    <div v-if="status === 'won'" class="hrd-overlay">
      <div class="hrd-overlay-emoji">🎉</div>
      <p class="hrd-overlay-title">通关啦！</p>
      <p class="hrd-overlay-info">
        {{ size }}×{{ size }} · {{ moves }} 步 · {{ seconds }} 秒
      </p>
      <div class="hrd-overlay-btns">
        <button type="button" class="hrd-btn hrd-btn-pink" @click="restart">再来一局</button>
        <button type="button" class="hrd-btn hrd-btn-line" @click="nextSize">挑战更大棋盘</button>
      </div>
    </div>
    <div v-if="status === 'lost'" class="hrd-overlay">
      <div class="hrd-overlay-emoji">⏰</div>
      <p class="hrd-overlay-title">时间到～</p>
      <p class="hrd-overlay-info">差一点点就完成啦，再试一次吧！</p>
      <div class="hrd-overlay-btns">
        <button type="button" class="hrd-btn hrd-btn-pink" @click="restart">再来一局</button>
        <button type="button" class="hrd-btn hrd-btn-line" @click="setMode('normal')">切到普通模式</button>
      </div>
    </div>
  </div>
</template>

<script>
/* =====================================================
 * 粉星数字华容道（复刻 y7x7 数字华容道）
 * 3×3 / 4×4 / 5×5，普通 + 限时挑战模式
 * 步数 / 计时 / 最佳纪录 / IDA* AI 提示
 * ===================================================== */
const BEST_KEY = 'pink_hrd_best'
const SIZES = [3, 4, 5]
const TIME_LIMIT = { 3: 90, 4: 240, 5: 420 }
const HINT_MAX = 3

export default {
  name: 'HuarongDao',
  data () {
    return {
      sizes: SIZES,
      size: 4,
      mode: 'normal',
      board: [],
      targetBoard: [],
      moves: 0,
      seconds: 0,
      timeLeft: TIME_LIMIT[4],
      hints: HINT_MAX,
      hintTile: -1,
      status: 'ready', // ready | playing | won | lost
      timer: null,
      best: {}
    }
  },
  computed: {
    boardStyle () {
      return {
        gridTemplateColumns: `repeat(${this.size}, 1fr)`,
        gridTemplateRows: `repeat(${this.size}, 1fr)`
      }
    }
  },
  created () {
    try {
      this.best = JSON.parse(localStorage.getItem(BEST_KEY)) || {}
    } catch (e) {
      this.best = {}
    }
    this.restart()
  },
  beforeDestroy () {
    this.stopTimer()
  },
  methods: {
    /* ---------- 初始化 ---------- */
    makeTarget () {
      const n = this.size
      const arr = []
      for (let i = 1; i < n * n; i++) arr.push(i)
      arr.push(0)
      return arr
    },
    restart () {
      this.stopTimer()
      this.board = this.shuffle()
      this.targetBoard = this.makeTarget()
      this.moves = 0
      this.seconds = 0
      this.timeLeft = TIME_LIMIT[this.size]
      this.hints = HINT_MAX
      this.hintTile = -1
      this.status = 'playing'
      this.startTimer()
    },
    shuffle () {
      // 从目标态做随机滑动，保证可解
      let arr = this.makeTarget()
      let empty = arr.indexOf(0)
      const n = this.size
      const count = n * n * (n === 3 ? 8 : n === 4 ? 14 : 22)
      let last = -1
      for (let i = 0; i < count; i++) {
        const r = Math.floor(empty / n)
        const c = empty % n
        const cands = []
        if (r > 0) cands.push(empty - n)
        if (r < n - 1) cands.push(empty + n)
        if (c > 0) cands.push(empty - 1)
        if (c < n - 1) cands.push(empty + 1)
        const valid = cands.filter(p => p !== last)
        const pick = valid[Math.floor(Math.random() * valid.length)]
        ;[arr[empty], arr[pick]] = [arr[pick], arr[empty]]
        last = empty
        empty = pick
      }
      // 若打乱后已复原，再打乱一次
      if (arr.every((v, i) => v === this.makeTarget()[i])) {
        return this.shuffle()
      }
      return arr
    },
    isInPlace (i, v) {
      return v === i + 1
    },
    tileStyle (i) {
      const n = this.size
      const r = Math.floor(i / n)
      const c = i % n
      return {
        transform: `translate(${c * (100 / n)}%, ${r * (100 / n)}%)`
      }
    },

    /* ---------- 计时 ---------- */
    startTimer () {
      this.stopTimer()
      this.timer = setInterval(() => {
        if (this.status !== 'playing') return
        if (this.mode === 'challenge') {
          this.timeLeft--
          if (this.timeLeft <= 0) {
            this.status = 'lost'
            this.stopTimer()
          }
        } else {
          this.seconds++
        }
      }, 1000)
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    /* ---------- 操作 ---------- */
    setSize (s) {
      if (s === this.size) return
      this.size = s
      this.restart()
    },
    setMode (m) {
      if (m === this.mode) return
      this.mode = m
      this.restart()
    },
    nextSize () {
      const idx = SIZES.indexOf(this.size)
      this.setSize(SIZES[Math.min(SIZES.length - 1, idx + 1)])
    },
    tileClick (i) {
      if (this.status !== 'playing') return
      if (this.board[i] === 0) return
      const empty = this.board.indexOf(0)
      const n = this.size
      const r = Math.floor(i / n)
      const c = i % n
      const er = Math.floor(empty / n)
      const ec = empty % n
      const adjacent = Math.abs(r - er) + Math.abs(c - ec) === 1
      if (!adjacent) return
      this.$set(this.board, empty, this.board[i])
      this.$set(this.board, i, 0)
      this.moves++
      this.hintTile = -1
      if (this.isWon()) {
        this.status = 'won'
        this.stopTimer()
        this.saveBest()
      }
    },
    isWon () {
      const target = this.makeTarget()
      return this.board.every((v, i) => v === target[i])
    },
    saveBest () {
      const rec = this.best[this.size] || {}
      const updated = {
        moves: rec.moves && rec.moves <= this.moves ? rec.moves : this.moves,
        sec: rec.sec && rec.sec <= this.seconds ? rec.sec : this.seconds
      }
      this.best[this.size] = updated
      try { localStorage.setItem(BEST_KEY, JSON.stringify(this.best)) } catch (e) {}
    },

    /* ---------- AI 提示 ---------- */
    useHint () {
      if (this.hints <= 0 || this.status !== 'playing') return
      const move = this.solveNextMove()
      if (move === -1) return
      this.hints--
      this.hintTile = move
      const self = this
      setTimeout(() => { self.hintTile = -1 }, 900)
    },
    // 返回“应移入空格的那个方块”的下标；找不到返回 -1
    solveNextMove () {
      const n = this.size
      const state = this.board.slice()
      const empty = state.indexOf(0)
      const res = this.idaStar(state, empty)
      if (res !== null) return res
      return this.greedyMove(state, empty)
    },
    // 曼哈顿距离 + 线性冲突（对 15 数码更有效的可采纳启发式）
    heuristic (state) {
      const n = this.size
      let d = 0
      const posOf = {}
      for (let i = 0; i < state.length; i++) {
        const v = state[i]
        if (v === 0) continue
        const tr = Math.floor((v - 1) / n)
        const tc = (v - 1) % n
        const r = Math.floor(i / n)
        const c = i % n
        posOf[i] = { tr, tc }
        d += Math.abs(tr - r) + Math.abs(tc - c)
      }
      // 行内线性冲突
      for (let r = 0; r < n; r++) {
        for (let a = 0; a < n; a++) {
          const ia = r * n + a
          if (!state[ia] || posOf[ia].tr !== r) continue
          for (let b = a + 1; b < n; b++) {
            const ib = r * n + b
            if (!state[ib] || posOf[ib].tr !== r) continue
            if (posOf[ia].tc > posOf[ib].tc) d += 2
          }
        }
      }
      // 列内线性冲突
      for (let c = 0; c < n; c++) {
        for (let a = 0; a < n; a++) {
          const ia = a * n + c
          if (!state[ia] || posOf[ia].tc !== c) continue
          for (let b = a + 1; b < n; b++) {
            const ib = b * n + c
            if (!state[ib] || posOf[ib].tc !== c) continue
            if (posOf[ia].tr > posOf[ib].tr) d += 2
          }
        }
      }
      return d
    },
    neighbors (empty, n) {
      const r = Math.floor(empty / n)
      const c = empty % n
      const list = []
      if (r > 0) list.push(empty - n)
      if (r < n - 1) list.push(empty + n)
      if (c > 0) list.push(empty - 1)
      if (c < n - 1) list.push(empty + 1)
      return list
    },
    idaStar (state, empty) {
      const n = this.size
      const budget = 500000
      const t0 = Date.now()
      const timeCap = 400 // 毫秒，避免提示时页面卡顿
      let nodes = 0
      let aborted = false
      let seen = new Map()
      // 返回：{found, move, limit, abort}
      const search = (s, e, g, limit, last) => {
        const h = this.heuristic(s)
        const f = g + h
        if (f > limit) return { limit: f }
        if (h === 0) return { found: true, move: null }
        // 转置剪枝：同一状态按更浅深度访问过则不再展开
        const key = s.join(',')
        const prev = seen.get(key)
        if (prev !== undefined && prev <= g) return { limit: Infinity }
        seen.set(key, g)
        if (g >= 60 || nodes > budget || Date.now() - t0 > timeCap) {
          aborted = true
          return { abort: true }
        }
        let min = Infinity
        const ns = this.neighbors(e, n)
        for (const p of ns) {
          if (p === last) continue
          ;[s[e], s[p]] = [s[p], s[e]]
          nodes++
          const r = search(s, p, g + 1, limit, e)
          ;[s[e], s[p]] = [s[p], s[e]]
          if (r.abort) return { abort: true }
          if (r.found) return { found: true, move: p }
          if (r.limit < min) min = r.limit
        }
        return { limit: min }
      }
      let limit = this.heuristic(state)
      while (limit <= 60) {
        aborted = false
        seen = new Map()
        const r = search(state, empty, 0, limit, -1)
        if (r.abort) return null
        if (r.found && r.move !== null) return r.move
        if (r.found) return null
        if (r.limit === Infinity || r.limit <= limit) return null
        limit = r.limit
      }
      return null
    },
    greedyMove (state, empty) {
      // 兜底贪心：选择使启发式距离减少最多、且自身最“错位”的方块
      const ns = this.neighbors(empty, this.size)
      let best = -1
      let bestDelta = Infinity
      let bestDist = -1
      const base = this.heuristic(state)
      for (const p of ns) {
        ;[state[empty], state[p]] = [state[p], state[empty]]
        const h = this.heuristic(state)
        ;[state[empty], state[p]] = [state[p], state[empty]]
        const delta = h - base
        const v = state[p]
        const n = this.size
        const tr = Math.floor((v - 1) / n)
        const tc = (v - 1) % n
        const r = Math.floor(p / n)
        const c = p % n
        const dist = Math.abs(tr - r) + Math.abs(tc - c)
        if (delta < bestDelta || (delta === bestDelta && dist > bestDist)) {
          bestDelta = delta
          bestDist = dist
          best = p
        }
      }
      return best
    }
  }
}
</script>

<style scoped>
.hrd {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 760px;
  margin: 0 auto;
}

/* ---------- 顶栏 ---------- */
.hrd-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.hrd-sizes {
  display: flex;
  gap: 6px;
}
.hrd-size, .hrd-mode {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 6px 14px;
  background: #fff;
  color: #b06a8a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.hrd-size.active, .hrd-mode.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
}
.hrd-modes { display: flex; gap: 6px; }
.hrd-stats {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}
.hrd-stat {
  font-size: 14px;
  font-weight: 600;
  color: #b06a8a;
}
.hrd-stat b { color: #ff5fa0; font-size: 16px; }
.hrd-stat.danger { color: #ff5f6d; animation: hrd-pulse 0.8s infinite; }
@keyframes hrd-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}
.hrd-hint {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 6px 14px;
  background: #fff;
  color: #e25b96;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.hrd-hint:hover:not(:disabled) { border-color: #ff6fa5; background: #ffeef6; }
.hrd-hint:disabled { opacity: 0.5; cursor: not-allowed; }
.hrd-refresh {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  width: 34px;
  height: 34px;
  background: #fff;
  color: #e25b96;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.hrd-refresh:hover { border-color: #ff6fa5; background: #ffeef6; }

/* ---------- 主体 ---------- */
.hrd-body {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
}
.hrd-board {
  position: relative;
  width: min(430px, 62vw);
  aspect-ratio: 1;
  display: grid;
  gap: 6px;
  padding: 12px;
  border-radius: 20px;
  background: linear-gradient(170deg, #ffe9f3, #ffd3e6);
  border: 2px solid #ffc3da;
  box-shadow: 0 10px 28px rgba(255, 158, 198, 0.3);
  box-sizing: border-box;
}
.hrd-tile {
  position: relative;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #ff9ec5, #ff6fa5);
  box-shadow: 0 3px 10px rgba(255, 111, 165, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  transition: transform 0.18s ease;
  cursor: pointer;
  user-select: none;
}
.hrd-tile.empty {
  background: rgba(255, 255, 255, 0.35);
  box-shadow: inset 0 0 0 2px dashed rgba(255, 158, 198, 0.6);
  cursor: default;
}
.hrd-tile.ok {
  background: linear-gradient(145deg, #e3b8ff, #b98aff);
  box-shadow: 0 3px 10px rgba(185, 138, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
.hrd-tile.hinted {
  animation: hrd-hint 0.8s ease infinite;
}
@keyframes hrd-hint {
  0%, 100% { box-shadow: 0 0 0 3px #ff3d8f, 0 4px 14px rgba(255, 61, 143, 0.55); transform: translateY(0); }
  50% { box-shadow: 0 0 0 6px rgba(255, 61, 143, 0.5), 0 4px 20px rgba(255, 61, 143, 0.7); transform: translateY(-4px); }
}
.hrd-tile-num {
  font-size: clamp(18px, 4vw, 34px);
  font-weight: 800;
  color: #fff;
  text-shadow: 0 2px 6px rgba(180, 40, 110, 0.45);
}
.hrd-tile-num-sm {
  font-size: clamp(12px, 2.4vw, 20px);
}

/* ---------- 侧栏 ---------- */
.hrd-side {
  width: 170px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.hrd-best, .hrd-target {
  padding: 12px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.hrd-side-title {
  font-size: 13px;
  font-weight: 700;
  color: #e25b96;
  margin-bottom: 8px;
}
.hrd-best-line {
  font-size: 13px;
  color: #b06a8a;
  display: flex;
  justify-content: space-between;
}
.hrd-best-line b { color: #ff5fa0; }
.hrd-target-grid {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  display: grid;
  gap: 3px;
  padding: 4px;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}
.hrd-target-cell {
  position: relative;
  border-radius: 5px;
  background: linear-gradient(145deg, #ffd0e4, #ff9ec5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.18s ease;
}
.hrd-target-cell.empty { background: rgba(255, 158, 198, 0.15); }
.hrd-tip {
  font-size: 12px;
  color: #c490a8;
  text-align: center;
}

/* ---------- 遮罩 ---------- */
.hrd-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 240, 247, 0.85);
  backdrop-filter: blur(3px);
}
.hrd-overlay-emoji { font-size: 56px; animation: hrd-bounce 0.5s ease; }
@keyframes hrd-bounce {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.hrd-overlay-title {
  font-size: 24px;
  font-weight: 800;
  color: #e25b96;
}
.hrd-overlay-info {
  font-size: 14px;
  color: #b06a8a;
}
.hrd-overlay-btns {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.hrd-btn {
  border: none;
  border-radius: 999px;
  padding: 10px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.hrd-btn-pink {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.35);
}
.hrd-btn-pink:hover { transform: translateY(-2px); }
.hrd-btn-line {
  background: #fff;
  border: 2px solid #ffd3e4;
  color: #b06a8a;
}
.hrd-btn-line:hover { border-color: #ff6fa5; color: #e25b96; }

@media (max-width: 700px) {
  .hrd-board { width: min(360px, 88vw); }
  .hrd-side { width: 100%; flex-direction: row; }
  .hrd-side > * { flex: 1; }
}
</style>
